import {
	type ActorRefFrom,
	assign,
	createMachine,
	type StateFrom,
} from 'xstate';
import { pure } from 'xstate/lib/actions';

import { sendToast } from 'components/Toast';
import { pushToGTM } from 'utils/GoogleTagManager';
import { i18n } from 'utils/i18n';
import { parseMarkdown } from 'utils/string';

import {
	addMultipleToWishlist,
	addOneToWishlist,
	fetchWishlist,
	fetchWishlistById,
	fetchWishlistMini,
	removeAllFromWishlist,
	removeVariantFromWishlist,
	updateVariantQtyInWishlist,
} from './wishlist.services';
import type {
	WishlistMachineContext,
	WishlistMachineEvents,
	WishlistMachineServices,
} from './wishlist.types';

export const wishlistMachine = createMachine(
	{
		id: 'wishlistMachine',
		initial: 'idle',
		type: 'parallel',
		tsTypes: {} as import('./wishlist.machine.typegen').Typegen0,
		predictableActionArguments: true,
		schema: {
			context: {} as WishlistMachineContext,
			events: {} as WishlistMachineEvents,
			services: {} as WishlistMachineServices,
		},
		context: {
			addedProductPopoverIsOpen: false,
			buttonState: {
				state: 'idle',
			},
			showToast: false,
		},
		states: {
			loadSharedWishlist: {
				id: 'loadSharedWishlist',
				initial: 'idle',
				states: {
					idle: {
						on: {
							FETCH_WISHLIST_BY_ID: 'loadingSharedWishlist',
						},
					},
					loadingSharedWishlist: {
						invoke: {
							id: 'getSharedWishlist',
							src: 'getSharedWishlist',
							onDone: {
								actions: 'setSharedWishlistData',
								target: 'idle',
							},
							onError: {
								target: 'idle',
							},
						},
					},
				},
			},
			loadPersonalWishlist: {
				id: 'loadPersonalWishlist',
				initial: 'initialLoadWishlist',
				states: {
					initialLoadWishlist: {
						tags: 'loading',
						on: {
							FETCH_MINI_WISHLIST: 'loadingMiniWishlist',
							FETCH_WISHLIST: {
								target: 'loadingWishlist',
								actions: 'setStoreId',
							},
						},
					},
					idle: {
						on: {
							FETCH_MINI_WISHLIST: 'loadingMiniWishlist',
							FETCH_WISHLIST: {
								target: 'loadingWishlist',
								actions: 'setStoreId',
							},
						},
					},
					loadingMiniWishlist: {
						tags: 'loading',
						on: {
							FETCH_WISHLIST: {
								target: 'loadingWishlist',
								actions: 'setStoreId',
							},
						},
						invoke: {
							id: 'getMiniWishlist',
							src: 'getMiniWishlist',
							onDone: {
								actions: 'setWishListProductQuantity',
								target: 'success',
							},
						},
					},
					loadingWishlist: {
						tags: 'loading',
						invoke: {
							id: 'getWishlist',
							src: 'getWishlist',
							onDone: {
								actions: 'setWishlistData',
								target: 'success',
							},
							onError: {
								target: 'error',
							},
						},
					},
					changingVariantQty: {
						tags: 'loading',
						invoke: {
							id: 'changeVariantQty',
							src: 'changeVariantQtyInWishlist',
							onDone: {
								actions: 'setWishlistData',
								target: 'updateGTM',
							},
							onError: {
								target: 'error',
							},
						},
					},
					settingVariantQty: {
						tags: 'loading',
						invoke: {
							id: 'setVariantQty',
							src: 'updateVariantQtyInWishlist',
							onDone: {
								actions: 'setWishlistData',
								target: 'updateGTM',
							},
							onError: {
								target: 'error',
							},
						},
					},
					removingVariant: {
						tags: 'loading',
						invoke: {
							id: 'removeVariant',
							src: 'removeVariantFromWishlist',
							onDone: {
								actions: 'setWishlistData',
								target: 'success',
							},
							onError: {
								target: 'error',
							},
						},
					},
					removingAll: {
						tags: 'loading',
						invoke: {
							id: 'removeAll',
							src: 'removeAllFromWishlist',
							onDone: {
								actions: 'resetWishlistData',
								target: 'success',
							},
							onError: {
								target: 'error',
							},
						},
					},
					success: {
						on: {
							FETCH_WISHLIST: {
								target: 'loadingWishlist',
								actions: 'setStoreId',
							},
							CHANGE_VARIANT_QTY: {
								target: 'changingVariantQty',
								actions: 'setGTMData',
							},
							SET_VARIANT_QTY: {
								target: 'settingVariantQty',
								actions: 'setGTMData',
							},
							REMOVE_VARIANT: 'removingVariant',
							REMOVE_ALL: 'removingAll',
						},
					},
					error: {
						on: {
							FETCH_WISHLIST: {
								target: 'loadingWishlist',
								actions: 'setStoreId',
							},
						},
					},
					updateGTM: {
						entry: 'updateGTM',
						exit: 'resetGTMData',
						always: {
							target: 'success',
						},
					},
				},
			},
			addMultipleToWishlist: {
				id: 'addMultipleToWishlist',
				initial: 'idle',
				states: {
					idle: {
						on: {
							ADD_MULTIPLE_VARIANTS_MINI: 'addingMultipleToWishlist',
						},
					},
					addingMultipleToWishlist: {
						invoke: {
							id: 'addMultipleToWishlist',
							src: 'addMultipleToWishlist',
							onDone: [
								{
									cond: 'isSuccess',
									actions: ['setWishListProductQuantity', 'showToastMessage'],
									target: 'idle',
								},
								{
									actions: 'showToastMessage',
									target: 'idle',
								},
							],
							onError: {
								target: 'idle',
							},
						},
					},
				},
			},
			addOneToWishlist: {
				id: 'addOneToWishlist',
				initial: 'idle',
				states: {
					idle: {
						on: {
							ADD_ONE_TO_WISHLIST: [
								{
									target: 'addingOneToWishlistWithPopover',
									cond: 'shouldOpenAddedProductPopover',
								},
								{
									target: 'addingOneToWishlist',
									actions: ['setShowToast', 'setGTMData'],
								},
							],
							RESET_ADDED_PRODUCT_POPOVER: {
								actions: 'resetAddedProductPopover',
							},
						},
					},
					addingOneToWishlist: {
						entry: 'setButtonStateLoading',
						exit: 'resetShowToast',
						invoke: {
							id: 'addOneToWishlist',
							src: 'addOneToWishList',
							onDone: [
								{
									target: 'updateGTM',
									cond: 'isSuccess',
									actions: [
										'setButtonState',
										'showToastMessage',
										'setWishListProductQuantity',
									],
								},

								{
									target: 'idle',
									actions: ['setButtonStateFailed', 'showToastMessage'],
								},
							],
							onError: [
								{
									target: 'idle',
									actions: 'setButtonStateFailed',
								},
							],
						},
					},
					addingOneToWishlistWithPopover: {
						entry: 'setButtonStateLoading',
						exit: 'resetShowToast',
						invoke: {
							id: 'addOneToWishlist',
							src: 'addOneToWishList',
							onDone: [
								{
									target: 'updateGTM',
									cond: 'isSuccess',
									actions: [
										'setButtonState',
										'setAdditionalSalesProducts',
										'setAddedProductPopoverOpen',
										'setMessage',
										'setAddedProduct',
										'setWishListProductQuantity',
									],
								},
								{
									target: 'idle',
									actions: ['setButtonStateFailed', 'showToastMessage'],
								},
							],
							onError: [
								{
									target: 'idle',
									actions: 'setButtonStateFailed',
								},
							],
						},
					},
					updateGTM: {
						entry: 'updateGTM',
						exit: 'resetGTMData',
						always: {
							target: 'idle',
						},
					},
				},
			},
		},
	},
	{
		services: {
			getWishlist: (_context, event) => fetchWishlist(event.storeId),
			getSharedWishlist: (_context, event) =>
				fetchWishlistById(event.id, event.storeId),
			getMiniWishlist: () => fetchWishlistMini(),
			removeAllFromWishlist: () => removeAllFromWishlist(),
			removeVariantFromWishlist: (context, event) =>
				removeVariantFromWishlist(event.variantId, context.storeId),
			updateVariantQtyInWishlist: (context, event) =>
				updateVariantQtyInWishlist({
					variantId: event.variantId,
					qty: event.qty,
					storeId: context.storeId,
					setDistinctValue: true,
				}),
			changeVariantQtyInWishlist: (context, event) =>
				updateVariantQtyInWishlist({
					variantId: event.variantId,
					qty: event.qty,
					storeId: context.storeId,
				}),
			addOneToWishList: (_context, event) =>
				addOneToWishlist(
					event.variantId,
					event.openAddedProductPopover ? 'true' : 'false',
				),
			addMultipleToWishlist: (_context, event) =>
				addMultipleToWishlist(event.variants),
		},
		guards: {
			shouldOpenAddedProductPopover: (_context, event) =>
				event.openAddedProductPopover,
			isSuccess: (_context, event) => event.data?.success ?? false,
		},
		actions: {
			setShowToast: assign({
				showToast: (_context, event) => event.showToast,
			}),
			resetShowToast: assign({
				showToast: (_context) => false,
			}),
			setWishListProductQuantity: assign({
				productQuantity: (_context, event) => event.data?.totalQty,
			}),
			setAddedProduct: assign({
				addedProduct: (_context, event) => event.data?.addedProducts?.[0],
			}),
			setWishlistData: assign({
				wishlistData: (_context, event) => event.data,
				productQuantity: (_context, event) => event.data?.totalQty,
			}),
			resetWishlistData: assign({
				wishlistData: (_context) => undefined,
				productQuantity: (_context) => undefined,
			}),
			setSharedWishlistData: assign({
				sharedWishlistData: (_context, event) => event.data,
			}),
			setStoreId: assign({
				storeId: (_context, event) => event.storeId,
			}),
			setButtonStateLoading: assign({
				buttonState: (_context, event) => ({
					state: 'loading' as const,
					buttonId: event.variantId,
				}),
			}),
			setButtonState: pure((_context, event) =>
				event.data?.success ? 'setButtonStateSuccess' : 'setButtonStateFailed',
			),
			// @ts-expect-error this is used in this pure func above
			// something the xstate typegen seems unable to figure out
			setButtonStateSuccess: assign({
				buttonState: (context) => ({
					state: 'success' as const,
					buttonId: context.buttonState.buttonId,
				}),
			}),
			setButtonStateFailed: assign({
				buttonState: (context) => ({
					state: 'failure' as const,
					buttonId: context.buttonState.buttonId,
				}),
			}),
			setMessage: assign({
				wishlistMiniMessage: (_context, event) => event.data?.message,
			}),
			setAddedProductPopoverOpen: assign({
				addedProductPopoverIsOpen: (_context) => true,
			}),
			setAdditionalSalesProducts: assign({
				additionalSalesProducts: (_context, event) => event.data?.accessories,
			}),
			resetAddedProductPopover: assign({
				addedProduct: (_context) => undefined,
				additionalSalesProducts: (_context) => undefined,
				addedProductPopoverIsOpen: (_context) => false,
			}),
			showToastMessage: (_, event) => {
				const addedProduct = event.data?.addedProducts?.[0];
				const message = addedProduct
					? parseMarkdown(
							i18n.t('variant_added_to_wishlist_text', {
								productTitle: addedProduct.title,
							}),
						)
					: event.data?.message;
				sendToast(
					message || '',
					event.data?.success ? 'success' : 'error',
					'/wishlist/',
					'continue_to_wishlist',
				);
			},
			updateGTM: (context) => {
				if (!context.GTMData) return;
				pushToGTM({
					type: context.GTMData.type,
					payload: { ...context.GTMData.payload },
				});
			},
			setGTMData: assign({
				GTMData: (_context, event) => event.GTMData,
			}),
			resetGTMData: assign({
				GTMData: (_context) => undefined,
			}),
		},
	},
);
export type WishlistMachineState = StateFrom<typeof wishlistMachine>;
export type WishlistMachineActor = ActorRefFrom<typeof wishlistMachine>;
